import type { ReactNode } from 'react';

import { SCROLL_COLLAPSE_EASING_DURATION_CLASS } from '~/components/ScrollCollapse/constants/classes';
import { useScrollCollapseContext } from '~/components/ScrollCollapse/hooks/useScrollCollapseContext';
import { cn } from '~/lib/cn';
import { ArtistBreadcrumbs } from '~/modules/artist-roster/pages/artist-details/components/ArtistHeader/components/ArtistBreadcrumbs';
import { TabLinks } from '~/modules/artist-roster/pages/artist-details/components/ArtistHeader/components/TabLinks';
import type { ArtistDetails } from '~/modules/artist-roster/types';

import { ArtistHeaderDetails } from './components/ArtistHeaderDetails';

interface ArtistHeaderProps {
  artistData?: ArtistDetails;
  children?: ReactNode;
}

export function ArtistHeader({ artistData, children }: ArtistHeaderProps) {
  const { headerRef, scrollProgress, getInterpolatedValue } =
    useScrollCollapseContext();
  const isHeaderCollapsed = scrollProgress > 1;

  return (
    <div
      className="sticky top-0 z-20 bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat px-xl pt-lg bg-level-01/full"
      ref={headerRef}
      aria-expanded={!isHeaderCollapsed}
    >
      <div
        className={cn(
          'will-change-[margin-bottom,height,opacity]',
          SCROLL_COLLAPSE_EASING_DURATION_CLASS,
          {
            'h-lg opacity-full': !isHeaderCollapsed,
            'pointer-events-none h-0 opacity-none': isHeaderCollapsed,
          },
        )}
        // Transition from "mb-lg" to "mb-0".
        style={{ marginBottom: getInterpolatedValue(24, 0) }}
      >
        <ArtistBreadcrumbs artistData={artistData} />
      </div>
      <ArtistHeaderDetails artistData={artistData} />
      <div
        className={cn(
          'flex w-full flex-col',
          SCROLL_COLLAPSE_EASING_DURATION_CLASS,
          {
            'h-[28px] opacity-full': !isHeaderCollapsed,
            'pointer-events-none h-0 opacity-none': isHeaderCollapsed,
          },
        )}
        // Transition from "mb-lg" to "12px".
        style={{ marginBottom: getInterpolatedValue(24, 12) }}
      >
        <TabLinks artistData={artistData} />
      </div>
      {children}
    </div>
  );
}

ArtistHeader.displayName = 'ArtistHeader';
